import { HttpStatusCode } from "axios";

import { LANG_DEFAULT, LANG_HEADER_NAME } from "../constants";
import { SimpleResult } from "../dto";
import { Api, getAccessToken, getCompanyId, readSseStream } from "../helpers";
import { isRydeApiRequestUri } from "../helpers/api-utils/ryde-api-axios-request.helpers";
import {
  readBrowserCookie,
  SESSION_CSRF_COOKIE_NAME,
  SESSION_CSRF_HEADER_NAME,
} from "../helpers/auth/session-cookie.constants";
import { refreshAccessToken } from "../helpers/auth/token-refresh.helpers";
import { apiBaseUrl } from "../helpers/env.helpers";
import { getLangLocalStorage } from "../helpers/rtl.helpers";
import { normalizeUrlBasePath } from "../helpers/url.helpers";
import {
  IAIQuestionDetailsResponse,
  IAIQuestionRequest,
  IAIQuestionResponse,
  IAiStreamCallbacks,
} from "../models";

const getRydeApiBaseUrlNormalized = (): string | undefined => {
  const raw = apiBaseUrl()?.trim();
  if (!raw) {
    return undefined;
  }
  return normalizeUrlBasePath(raw);
};

export class AiAdvisorApi {
  static async question(
    body: IAIQuestionRequest,
  ): Promise<SimpleResult<IAIQuestionResponse> | undefined> {
    const requestUrl = `/api/v1/core/c/${getCompanyId()}/ai-advisor/question`;
    const response = await Api.post<
      SimpleResult<IAIQuestionResponse> | undefined
    >(requestUrl, body);
    return response?.data;
  }

  static async questionStream(
    body: IAIQuestionRequest,
    callbacks: IAiStreamCallbacks,
    signal?: AbortSignal,
  ): Promise<void> {
    const base = normalizeUrlBasePath(apiBaseUrl() || "");
    const url = `${base}/api/v1/core/c/${getCompanyId()}/ai-advisor/question/stream`;

    // Built per attempt: a session refresh replaces the CSRF cookie and the token.
    const send = (): Promise<Response> => {
      const token = getAccessToken();

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        [LANG_HEADER_NAME]: getLangLocalStorage() || LANG_DEFAULT,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const csrf = readBrowserCookie(SESSION_CSRF_COOKIE_NAME);
      if (
        csrf &&
        isRydeApiRequestUri({
          requestUri: url,
          rydeApiBase: getRydeApiBaseUrlNormalized(),
        })
      ) {
        headers[SESSION_CSRF_HEADER_NAME] = csrf;
      }

      return fetch(url, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(body),
        signal,
      });
    };

    const firstResponse = await send();
    // An expired access token gets one refresh and retry, as axios calls do. The
    // access token lives 15 minutes, so a question asked after a quiet spell on the
    // page used to fail with "Something went wrong" until the page was reloaded.
    const response =
      firstResponse.status === HttpStatusCode.Unauthorized
        ? await refreshAccessToken().then(send)
        : firstResponse;

    if (!response.ok || !response.body) {
      callbacks.onError(`HTTP ${response.status}`);
      return;
    }

    await readSseStream(response.body.getReader(), callbacks);
  }

  static async getDetails(
    sessionId: string,
  ): Promise<SimpleResult<IAIQuestionDetailsResponse> | undefined> {
    const requestUrl = `/api/v1/core/c/${getCompanyId()}/ai-advisor/session/${sessionId}/details`;
    const response = await Api.get<
      SimpleResult<IAIQuestionDetailsResponse> | undefined
    >(requestUrl);
    return response?.data;
  }
}
